import { useEffect, useLayoutEffect, useState } from "react"
import { useAppContext } from "../../../contexts/app";
import { Button, ButtonLight, ButtonPrimary, ButtonSecondary } from "../../../components/ctas";
import { comparePDIToKeys, downloadJSONFile, generateReportAsJSON } from "../inventory2/page";
import { Textarea } from "../../../components/forms";
import { PDI } from "../data/pdi";

// Helper function to get readable name from slug/key
const getReadableNameFromKey = (key) => {
	let readableName = null;

	// Recursively search through PDI data structure
	const searchPDI = (categories) => {
		categories?.forEach(category => {
			category?.sections?.forEach(section => {
				section?.items?.forEach(item => {
					// Check if this is the item slug we're looking for
					if (item.slug === key) {
						readableName = item.name;
						return;
					}
					// Check form fields
					item?.form?.forEach(formField => {
						if (formField.id === key) {
							readableName = formField.label || formField.id;
							return;
						}
					});
				});
			});
		});
	};

	if (PDI?.flockTrailer?.v1) {
		searchPDI(PDI.flockTrailer.v1);
	}

	return readableName || key; // fallback to key if no readable name found
};

// Helper function to format date headers
const formatDateHeader = (dateKey) => {
	// dateKey is in format "YYYY-MM-DD" from local timezone
	const [year, month, day] = dateKey.split('-').map(Number);

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	// Create date in local timezone
	const compareDate = new Date(year, month - 1, day);

	const dateString = compareDate.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	});

	if (compareDate.getTime() === today.getTime()) {
		return `Today (${dateString})`;
	} else if (compareDate.getTime() === yesterday.getTime()) {
		return `Yesterday (${dateString})`;
	} else {
		return dateString;
	}
};

const Include = ({ setItemsToExport, itemsToExport, unit }) => {
	const [active, setActive] = useState(false);

	const addToItems = (e, item) => {
		e.preventDefault();

		setItemsToExport(prevItems => {
			if (!prevItems) {
				return [item];
			} else if (prevItems.includes(item)) {
				return prevItems.filter(i => i !== item);
			} else {
				return [...prevItems, item];
			}
		});

		setActive(!active);
	};

	return <>
		<Button className={`tw-min-w-30px tw-min-h-30px tw-text-center tw-justify-center tw-items-center tw-border-1px tw-border-solid ${active ? 'tw-bg-white tw-text-black' : 'tw-bg-transparent tw-border-white'}`} onClick={(e) => addToItems(e, unit)}>
			<span className="tw-pointer-events-none tw-w-full tw-flex tw-justify-center tw-items-center">
				{active ? <>&#10003;</> : <> </>}
			</span>
		</Button>
	</>
}

// Helper function to get date key (YYYY-MM-DD) from a date string
// Parse as local time to avoid timezone issues
const getDateKey = (dateString) => {
	// Expected format: YYYY-MM-DD
	if (!dateString || typeof dateString !== 'string') return null;

	const [year, month, day] = dateString.split('-').map(Number);

	// Validate parsed values
	if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
		return null;
	}

	// Return the date key as-is since it's already in YYYY-MM-DD format
	return dateString;
};

// Helper function to sort units by last 6 digits of VIN in ascending order
const sortUnitsByVin = (units) => {
	return [...units].sort((a, b) => {
		const vinA = a.VIN || '';
		const vinB = b.VIN || '';
		const last6A = vinA.slice(-6);
		const last6B = vinB.slice(-6);

		// Try to parse as numbers for numeric comparison
		const numA = parseInt(last6A, 10);
		const numB = parseInt(last6B, 10);

		// If both are valid numbers, compare numerically
		if (!isNaN(numA) && !isNaN(numB)) {
			return numA - numB;
		}

		// Otherwise, compare as strings
		return last6A.localeCompare(last6B);
	});
};

export default function ShippingUnits() {
	const { loadSheetAsJson } = useAppContext();
	const [allUnits, setAllUnits] = useState(null);
	const [filteredUnits, setFilteredUnits] = useState(null);
	const [groupedUnits, setGroupedUnits] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [expandedUnits, setExpandedUnits] = useState({});
	const [expandedStatusReports, setExpandedStatusReports] = useState({});
	const [itemsToExport, setItemsToExport] = useState(null);

	useLayoutEffect(() => {
		setIsLoading(true);
		const rows = loadSheetAsJson();
		rows.then((rows) => {
			// Filter units based on shipping date field
			const shippingUnits = rows.filter((unit) => {
				const shippingDate = unit.logistics_shipping_information_shipping_date_shipping_date;
				if (!shippingDate || shippingDate === '') return false;

				// Try to parse the date to ensure it's valid
				const date = new Date(shippingDate);
				return !isNaN(date.getTime());
			});

			// Sort by shipping date (most recent first)
			const sorted = shippingUnits.sort((a, b) => {
				const dateA = new Date(a.logistics_shipping_information_shipping_date_shipping_date);
				const dateB = new Date(b.logistics_shipping_information_shipping_date_shipping_date);
				return dateB - dateA;
			});

			// Limit to 200 units
			const limited = sorted.slice(0, 200);

			setAllUnits(limited);
			setFilteredUnits(limited);
			groupUnitsByDay(limited);
		}).finally(() => {
			setIsLoading(false);
		});
	}, []);

	const groupUnitsByDay = (units) => {
		const grouped = {};

		units.forEach((unit) => {
			const dateKey = getDateKey(unit.logistics_shipping_information_shipping_date_shipping_date);
			if (dateKey) {
				if (!grouped[dateKey]) {
					grouped[dateKey] = [];
				}
				grouped[dateKey].push(unit);
			}
		});

		setGroupedUnits(grouped);
	};

	const handleSearch = (searchValue) => {
		setSearchTerm(searchValue);
		const val = searchValue.toLowerCase();

		if (!val || val === '') {
			setFilteredUnits(allUnits);
			groupUnitsByDay(allUnits);
			return;
		}

		const filtered = (allUnits || []).filter((unit) => {
			return (unit.unit_identifiers_unit_identifier_name && unit.unit_identifiers_unit_identifier_name.toLowerCase().includes(val)) ||
				(unit.VIN && unit.VIN.toLowerCase().includes(val)) ||
				(unit.UUID && unit.UUID.toLowerCase().includes(val));
		});

		setFilteredUnits(filtered);
		groupUnitsByDay(filtered);
	};

	// Get sorted date keys (most recent first)
	const sortedDateKeys = Object.keys(groupedUnits).sort((a, b) => {
		return new Date(b) - new Date(a);
	});

	// Check if there are any units shipping today
	const today = new Date();
	const year = today.getFullYear();
	const month = String(today.getMonth() + 1).padStart(2, '0');
	const day = String(today.getDate()).padStart(2, '0');
	const todayKey = `${year}-${month}-${day}`;
	const hasUnitsToday = groupedUnits[todayKey] && groupedUnits[todayKey].length > 0;

	useEffect(() => {
		console.log("itemsToExport updated:", itemsToExport);
	}, [itemsToExport]);


	const sexportAllItems = (type) => {
		if (!itemsToExport || itemsToExport.length === 0) {
			alert("No items selected for export.");
			return;
		}

		if (type === 'pdf') {
			// Generate PDFs for selected units
			itemsToExport.forEach(unit => {
				const pdfUrl = `/fs/flock/pdf/${unit.UUID}/${unit.Version}`;
				// Open each PDF in a new tab
				window.open(pdfUrl, '_blank');
			});
		}

		if (type === 'json') {
			console.log("Exporting JSON for items:", itemsToExport);

			itemsToExport.forEach(unit => {
				downloadJSONFile(generateReportAsJSON(unit), `RVMP-Flex-Security_${unit.unit_identifiers_unit_model ? unit.unit_identifiers_unit_model.replaceAll(" ", "-").toUpperCase() : ""}_${unit.Company ? unit.Company.replaceAll(" ", "-") : ""}_${unit.unit_identifiers_unit_identifier_name ? unit.unit_identifiers_unit_identifier_name.replaceAll(" ", "-").toUpperCase() : ""}_${unit["Updated On"] ? new Date(unit["Updated On"]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).replaceAll(" ", "_").replaceAll(",", "_") : ""}.json`);
			});
		}
	}

	return <>
		<div className="tw-w-full">
			{/* Loading Overlay */}
			{isLoading && (
				<div className="tw-fixed tw-inset-0 tw-bg-black/80 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-backdrop-blur-sm">
					<div className="tw-bg-white tw-rounded-6px tw-p-40px tw-shadow-2xl tw-flex tw-flex-col tw-items-center tw-gap-20px">
						<div className="tw-w-60px tw-h-60px tw-border-4px tw-border-solid tw-border-white/20 tw-border-t-secondary tw-rounded-full tw-animate-spin"></div>
						<div className="tw-text-secondary tw-font-secondary-bold tw-text-xl tw-uppercase tw-tracking-widest">
							Loading...
						</div>
					</div>
				</div>
			)}

			<h1 className="tw-text-3xl tw-font-secondary-bold tw-tracking-widest tw-text-white tw-px-10px tw-text-center tw-py-20px tw-leading-none tw-uppercase tw-mb-20d">
				Unit Shipping Report
			</h1>

			{/* Search Box */}
			<div>
				<form className="tw-m-0px tw-px-0px tw-pt-0px tw-pb-20px" onSubmit={(e) => {
					e.preventDefault();
					return false;
				}}>
					<input
						type="text"
						name="search"
						id="search"
						value={searchTerm}
						className="tw-outline-none tw-w-full tw-bg-black tw-border tw-border-white/20 tw-rounded tw-p-10px tw-text-white tw-mb-20px"
						placeholder="Search by Name, VIN, or UUID"
						onChange={(e) => handleSearch(e.target.value)}
					/>
					<button type="submit" className="tw-hidden" />
				</form>
			</div>

			<div>
				<div className="tw-text-center">
					Bulk Export {itemsToExport && itemsToExport.length > 0 ? `(${itemsToExport.length} selected)` : ''}
				</div>
				<div className="tw-flex tw-w-full tw-justify-between tw-mt-10px tw-pt-10px tw-gap-10px tw-border-t tw-border-white/20">
					<ButtonLight
						default
						className={`tw-min-h-40px tw-min-w-0px tw-basis-1/3 md:tw-min-w-[7.8125em] ${(itemsToExport && itemsToExport.length > 0) ? '' : 'tw-opacity-50 !tw-pointer-events-none !tw-cursor-default'}`}
						title="Download PDFs"
						onClick={(e) => {
							e.preventDefault();
							sexportAllItems('pdf');
						}}>
						PDF
					</ButtonLight>

					<ButtonLight default className={`tw-min-h-40px tw-min-w-0px tw-basis-1/3  md:tw-min-w-[7.8125em] ${(itemsToExport && itemsToExport.length > 0) ? '' : 'tw-opacity-50 !tw-pointer-events-none !tw-cursor-default'}`} title="Download JSON" onClick={(e) => {
						e.preventDefault();
						sexportAllItems('json');
					}}>
						JSON
					</ButtonLight>

					<ButtonSecondary
						className={`tw-cursor-pointer tw-border-white/90 [&_span]:tw-flex-grow hover:!tw-bg-black tw-text-center hover:!tw-text-white tw-px-10px tw-py-2px tw-min-h-40px tw-items-center tw-items-justify tw-min-w-0px tw-basis-1/3 md:tw-min-w-[7.8125em] ${filteredUnits && filteredUnits.length > 0 ? '' : 'tw-opacity-50 !tw-pointer-events-none !tw-cursor-default'}`}
						disabled={!filteredUnits || filteredUnits.length === 0 ? 'disabled' : null}
						onClick={e => {
							e.preventDefault();
							const sortedUnits = sortUnitsByVin(filteredUnits);
							const allVins = sortedUnits.map(u => u.VIN).filter(v => v && v !== '').join('\n');
							navigator.clipboard.writeText(allVins || '').then(() => {
								alert(`Copied ${allVins.split('\n').length} VIN${allVins.split('\n').length !== 1 ? 's' : ''} to clipboard:\n${allVins}`);
							}).catch(err => {
								alert('Failed to copy VINs: ', err);
							});
						}}>
						<span className="tw-text-[.65em] tw-pointer-events-none tw-uppercase tw-flex tw-flex-col tw-items-center tw-items-justify tw-flex-grow tw-w-full">
							<span className="tw-w-full">Copy All {filteredUnits && filteredUnits.length > 0 ? filteredUnits.filter(u => u.VIN && u.VIN !== '').length : 0} VIN{(filteredUnits && filteredUnits.filter(u => u.VIN && u.VIN !== '').length !== 1) ? 's' : ''}</span>
						</span>
					</ButtonSecondary>
				</div>
			</div>

			{/* Results Summary */}
			{filteredUnits && filteredUnits.length > 0 && (
				<div className="tw-text-white tw-font-quaternary-lite tw-text-[.9em] tw-my-10px tw-text-center">
					{filteredUnits.length} shipping {filteredUnits.length === 1 ? "unit" : "units"} found (showing max 200).
				</div>
			)}

			{/* Today's Status */}
			{!isLoading && !hasUnitsToday && (
				<div className="tw-text-yellow-500 tw-font-quaternary tw-text-[1.1em] tw-my-20px tw-text-center tw-p-20px tw-border tw-border-yellow-500/30 tw-rounded tw-bg-yellow-500/10">
					No units shipping today.
				</div>
			)}

			{/* Units Grouped by Day */}
			<div className="tw-space-y-40px tw-pb-40px">
				{sortedDateKeys.length > 0 ? (
					sortedDateKeys.map((dateKey) => {
						const unitsForDay = groupedUnits[dateKey];
						const dateHeader = formatDateHeader(dateKey);

						const sortedUnitsForDay = sortUnitsByVin(unitsForDay);
						const vinsForDay = sortedUnitsForDay.map(u => u.VIN).filter(v => v && v !== '').join('\n');

						// Generate formatted status report for each unit in the day
						const statusReportForDay = sortedUnitsForDay.map(unit => {
							const statusOfUnit = comparePDIToKeys(unit);
							const unitName = unit.unit_identifiers_unit_identifier_name || 'No Name';
							const vin = unit.VIN || 'N/A';
							let report = `${unitName} (${vin})`;

							// If unit has missing items, show them
							if (statusOfUnit.missingItems !== 0) {
								// Not Passed items
								if (statusOfUnit?.needsToBeCompletedKeys?.length > 0) {
									report += '\n- Not Passed:';
									statusOfUnit.needsToBeCompletedKeys.forEach(key => {
										report += `\n-- ${getReadableNameFromKey(key)}`;
									});
								}

								// Not Started items
								if (statusOfUnit?.missingKeys?.length > 0) {
									report += '\n- Not Started:';
									statusOfUnit.missingKeys.forEach(key => {
										report += `\n-- ${getReadableNameFromKey(key)}`;
									});
								}
							} else {
								// Unit is complete with no missing items
								report += '\nUnit complete';
							}

							return report;
						}).join('\n\n');

						return <>
							<div className="tw-border tw-p-10px tw-border-white/30 tw-rounded tw-bg-black/50" key={dateKey}>
								<div className="tw-space-y-20px">
									{/* Date Header */}
									<h2 className="tw-text-2xl tw-font-secondary-bold tw-tracking-widest tw-text-white tw-px-10px tw-py-10px tw-leading-none tw-uppercase tw-border-b tw-border-white/30 tw-sticky tw-top-0px tw-bg-black/90 tw-backdrop-blur-sm tw-z-10">
										{dateHeader}
										<span className="tw-text-sm tw-font-quaternary-lite tw-ml-10px tw-normal-case">
											({unitsForDay.length} {unitsForDay.length === 1 ? "unit" : "units"})
										</span>
									</h2>
									<Textarea value={vinsForDay} className="tw-w-full tw-mt-10px tw-hidden" />
									{expandedStatusReports[dateKey] && (
										<Textarea value={statusReportForDay} className="tw-w-full tw-mt-10px" />
									)}
									<div className="tw-grid tw-grid-cols-2 tw-gap-20px">


										<ButtonSecondary className={`tw-cursor-pointer tw-border-white/90 [&_span]:tw-flex-grow hover:!tw-bg-black tw-text-center hover:!tw-text-white tw-px-10px tw-py-2px tw-min-h-25px  tw-items-center tw-items-justify ${vinsForDay ? '' : 'tw-opacity-50 !tw-pointer-events-none !tw-cursor-default'}`} disabled={!vinsForDay ? 'disabled' : null} onClick={e => {
											e.preventDefault();
											navigator.clipboard.writeText(vinsForDay || '').then(() => {
												// alert success
												alert(`Copied to clipboard: \n${vinsForDay}`);
											}).catch(err => {
												// alert error
												alert('Failed to copy VINs: ', err);
											});
										}}>
											<span className="tw-text-[.65em] tw-pointer-events-none tw-uppercase tw-flex tw-flex-col tw-items-center tw-items-justify tw-flex-grow tw-w-full">
												<span className="tw-w-full">Copy {vinsForDay.split('\n').length} VIN{vinsForDay.split('\n').length !== 1 ? 's' : ''}</span>
												{/* <span className="tw-w-full tw-border-t-1px tw-border-white/20">{dateHeader}</span> */}
											</span>
										</ButtonSecondary>

										<ButtonSecondary className={`tw-cursor-pointer tw-border-white/90 [&_span]:tw-flex-grow hover:!tw-bg-black tw-text-center hover:!tw-text-white tw-px-10px tw-py-2px tw-min-h-25px  tw-items-center tw-items-justify ${statusReportForDay ? '' : 'tw-opacity-50 !tw-pointer-events-none !tw-cursor-default'}`} disabled={!statusReportForDay ? 'disabled' : null} onClick={e => {
											e.preventDefault();
											navigator.clipboard.writeText(statusReportForDay || '').then(() => {
												// alert success
												alert(`Copied to clipboard: \n${statusReportForDay}`);
											}).catch(err => {
												// alert error
												alert('Failed to copy unit status: ', err);
											});
										}}>
											<span className="tw-text-[.65em] tw-pointer-events-none tw-uppercase">Copy items to complete</span>
										</ButtonSecondary>
									</div>
									{sortedUnitsForDay.map((unit, idx) => {
										const statusOfUnit = comparePDIToKeys(unit);
										const unitKey = `${dateKey}-${idx}`;
										const isExpanded = expandedUnits[unitKey] || false;

										return (
											<div key={idx} className="tw-flex tw-leading-none tw-font-quaternary tw-font-quaternary-thin tw-flex-col tw-border tw-border-white/20 tw-rounded tw-bg-black tw-p-20px tw-invert-0 hover:tw-bg-white/15">
												<div className="tw-flex tw-w-full tw-gap-20px">
													<div className="tw-flex-shrink-0">
														<Include unit={unit} setItemsToExport={setItemsToExport} itemsToExport={itemsToExport} />
													</div>
													<div className="tw-flex tw-flex-grow tw-flex-col">
														<div className="tw-font-bold tw-text-[1.5em] tw-mb-5p">
															&quot;<span className="tw-uppercase">{unit.unit_identifiers_unit_identifier_name || 'No Name'}&quot; Trailer</span>
														</div>
														<div className="tw-opacity-80 tw-mb-10p">
															VIN: <span className="tw-font-code tw-normal-case tw-text-[.95em] tw-font-bold">{unit.VIN || 'N/A'}</span>
														</div>

														{/* Unit Details */}
														<div className="tw-mt-10px tw-uppercase tw-space-y-10p">
															{statusOfUnit.missingItems !== 0 && (
																<div className="tw-text-[.9em] tw-space-y-5px">
																	<div
																		className="tw-cursor-pointer tw-flex tw-items-center tw-gap-5px hover:tw-opacity-80"
																		onClick={() => setExpandedUnits(prev => ({ ...prev, [unitKey]: !prev[unitKey] }))}
																	>
																		<span className="tw-select-none">
																			{isExpanded ? '▼' : '▶'}
																		</span>
																		<span>
																			Items to Complete: <span className="tw-font-bold">{statusOfUnit.missingItems - statusOfUnit?.missingKeys?.length} &quot;Not Passed&quot; <span className="tw-font-quaternary-lite">( <span className="tw-font-bold">{statusOfUnit?.missingKeys?.length} Not Started</span> )</span></span>
																		</span>
																	</div>

																	{isExpanded && (
																		<div className="tw-ml-20px tw-mt-5px tw-space-y-5px tw-text-[0.85em] tw-normal-case">
																			{statusOfUnit?.needsToBeCompletedKeys?.length > 0 && (
																				<div>
																					<div className="tw-font-bold tw-text-red-400 tw-mb-3px">Not Passed:</div>
																					<ul className="tw-list-disc tw-ml-20px tw-space-y-2px">
																						{statusOfUnit.needsToBeCompletedKeys.slice(0, 10).map((key, i) => (
																							<li key={i} className="tw-opacity-90">
																								{getReadableNameFromKey(key)}
																							</li>
																						))}
																						{statusOfUnit.needsToBeCompletedKeys.length > 10 && (
																							<li className="tw-opacity-70 tw-italic">
																								... {statusOfUnit.needsToBeCompletedKeys.length - 10} more items hidden
																							</li>
																						)}
																					</ul>
																				</div>
																			)}
																			{statusOfUnit?.missingKeys?.length > 0 && (
																				<div>
																					<div className="tw-font-bold tw-text-yellow-400 tw-mb-3px tw-mt-5px">Not Started:</div>
																					<ul className="tw-list-disc tw-ml-20px tw-space-y-2px">
																						{statusOfUnit.missingKeys.slice(0, 10).map((key, i) => (
																							<li key={i} className="tw-opacity-90">
																								{getReadableNameFromKey(key)}
																							</li>
																						))}
																						{statusOfUnit.missingKeys.length > 10 && (
																							<li className="tw-opacity-70 tw-italic">
																								... {statusOfUnit.missingKeys.length - 10} more items hidden
																							</li>
																						)}
																					</ul>
																				</div>
																			)}
																		</div>
																	)}
																</div>
															)}
															{unit.logistics_shipping_information_shipping_date_shipping_date && (
																<div className="tw-text-[.9em]">
																	Ship Date: {unit.logistics_shipping_information_shipping_date_shipping_date}
																</div>
															)}
														</div>
													</div>
												</div>

												{/* Action Buttons */}
												<div className="tw-flex tw-w-full tw-justify-between tw-mt-10px tw-pt-10px tw-gap-10px tw-border-t tw-border-white/20">
													<ButtonLight
														default
														className="tw-min-h-40px tw-min-w-0px tw-basis-1/4 md:tw-min-w-[7.8125em]"
														href={`/fs/flock/pdf/${unit.UUID}/${unit.Version}`}
														title="Print PDF"
														target="_blank">
														PDF
													</ButtonLight>

													<ButtonLight default className="tw-min-h-40px tw-min-w-0px tw-basis-1/4  md:tw-min-w-[7.8125em]" title="Download JSON" onClick={(e) => {
														e.preventDefault();
														downloadJSONFile(generateReportAsJSON(unit), `RVMP-Flex-Security_${unit.unit_identifiers_unit_model ? unit.unit_identifiers_unit_model.replaceAll(" ", "-").toUpperCase() : ""}_${unit.Company ? unit.Company.replaceAll(" ", "-") : ""}_${unit.unit_identifiers_unit_identifier_name ? unit.unit_identifiers_unit_identifier_name.replaceAll(" ", "-").toUpperCase() : ""}_${unit["Updated On"] ? new Date(unit["Updated On"]).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).replaceAll(" ", "_").replaceAll(",", "_") : ""}.json`);
													}}>
														JSON
													</ButtonLight>

													<ButtonLight
														default
														className="tw-min-h-40px tw-min-w-0px tw-basis-1/4 md:tw-min-w-[7.8125em]"
														href={unit?.victron_vrm_link}
														title="Print PDF"
														target="_blank">
														VRM
													</ButtonLight>



													<ButtonLight
														default
														className="tw-min-h-40px tw-min-w-0px tw-basis-1/4 md:tw-min-w-[7.8125em]"
														href={`/fs/flock-edit/${unit.UUID}/${unit.Version}`}
														title="Edit unit">
														View
													</ButtonLight>
												</div>
											</div>
										);
									})}
								</div>

							</div>


						</>;
					})
				) : (
					<div className="tw-text-white tw-font-quaternary tw-text-center tw-p-20px">
						{searchTerm ? "No shipping units found matching your search." : "No shipping units found."}
					</div>
				)}
			</div>
		</div>
	</>
}
